import React, { useState } from 'react';
import {
  TableRow,
  TableCell,
  Typography,
  TextField,
  Autocomplete,
  IconButton,
  Box,
  Chip,
  Switch,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Button,
  Checkbox,
  Collapse,
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import CurrencyInput from './../commons/CurrencyInput';
import CorrelationBadge from './CorrelationBadge';
import CorrelatedExpensesList from './CorrelatedExpensesList';
import CorrelationSelector from './CorrelationSelector';
import RecurringLinkSelect from '../budget/RecurringLinkSelect';
import { formatValue, toInputDate, toDisplayDate } from '../../utils/utils';

const EditableRow = React.memo(({ item, categories, onUpdate, onDelete, onError, selected = false, onSelect, onCorrelationUpdate, recurringItems = [], onLinkUpdate }) => {
  const [isEditing, setIsEditing] = useState({
    description: false,
    category: false,
    observation: false,
    value: false,
    debitDate: false,
  });
  const [description, setDescription] = useState(item.Description || '');
  const [selectedCategory, setSelectedCategory] = useState(item.Category || {});
  const [observation, setObservation] = useState(item.Observation || '');
  const [isMandatory, setIsMandatory] = useState(item.IsMandatory || false);
  const [value, setValue] = useState(Number(item.Value) || 0);
  const [debitDate, setDebitDate] = useState(toInputDate(item.DebitDate));
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [expanded, setExpanded] = useState(false);
  const [selectorOpen, setSelectorOpen] = useState(false);
  const [correlationKey, setCorrelationKey] = useState(0);

  const handleCorrelationChange = (hasCorrelations, linkedCandidate) => {
    // When linking via selector, refresh the correlations list
    if (linkedCandidate) {
      setCorrelationKey((prev) => prev + 1);
    }
    // Update parent's HasCorrelations flag optimistically
    if (hasCorrelations !== undefined) {
      onCorrelationUpdate?.(item.ID, { HasCorrelations: hasCorrelations });
    }
  };

  const handleEditClick = (field) => {
    setIsEditing((prev) => ({ ...prev, [field]: true }));
  };

  const handleCancelEdit = (field) => {
    if (field === 'description') setDescription(item.Description || '');
    if (field === 'category') setSelectedCategory(item.Category || {});
    if (field === 'observation') setObservation(item.Observation || '');
    if (field === 'value') setValue(Number(item.Value) || 0);
    if (field === 'debitDate') setDebitDate(toInputDate(item.DebitDate));
    setIsEditing((prev) => ({ ...prev, [field]: false }));
  };

  const handleSave = async (field) => {
    const isDescriptionChanged = description !== item.Description;
    const isCategoryChanged = selectedCategory?.ID !== item.Category?.ID;
    const isObservationChanged = observation !== item.Observation;
    const isValueChanged = value !== Number(item.Value);
    const isDebitDateChanged = debitDate !== toInputDate(item.DebitDate);

    if (isDescriptionChanged || isCategoryChanged || isObservationChanged || isValueChanged || isDebitDateChanged) {
      await onUpdate(item.ID, description, selectedCategory?.ID, observation, isMandatory, value, debitDate);
    }

    setIsEditing((prev) => ({ ...prev, [field]: false }));
  };

  const handleMandatoryChange = async () => {
    const newValue = !isMandatory;
    setIsMandatory(newValue);
    await onUpdate(item.ID, description, selectedCategory?.ID, observation, newValue, value, debitDate);
  };

  const handleConfirmDelete = () => {
    onDelete(item.ID);
    setDeleteOpen(false);
  };

  return (
    <>
    <TableRow hover>
      <TableCell padding="checkbox">
        <Checkbox
          checked={selected}
          onChange={onSelect}
          size="small"
        />
      </TableCell>
      <TableCell sx={{ minWidth: 150 }}>
        {isEditing.description ? (
          <TextField
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            onBlur={() => handleSave('description')}
            onKeyDown={(e) => {
              if (e.key === 'Escape') handleCancelEdit('description');
              if (e.key === 'Enter') handleSave('description');
            }}
            autoFocus
            fullWidth
            size="small"
            variant="outlined"
          />
        ) : (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Typography
              onClick={() => handleEditClick('description')}
              sx={{ cursor: 'pointer', '&:hover': { color: 'primary.main' } }}
            >
              {description || '-'}
            </Typography>
            <CorrelationBadge isCorrelated={item.IsCorrelated} hasCorrelations={item.HasCorrelations} />
          </Box>
        )}
      </TableCell>

      <TableCell sx={{ minWidth: 120 }}>
        {isEditing.category ? (
          <Autocomplete
            options={categories}
            getOptionLabel={(option) => option?.Name || ''}
            value={selectedCategory}
            onChange={(event, newValue) => setSelectedCategory(newValue)}
            onBlur={() => handleSave('category')}
            isOptionEqualToValue={(option, val) => option?.ID === val?.ID}
            renderInput={(params) => (
              <TextField {...params} autoFocus fullWidth size="small" variant="outlined" />
            )}
            size="small"
          />
        ) : (
          <Typography
            onClick={() => handleEditClick('category')}
            sx={{ cursor: 'pointer', '&:hover': { color: 'primary.main' } }}
          >
            {selectedCategory?.Name || '-'}
          </Typography>
        )}
      </TableCell>

      <TableCell sx={{ display: { xs: 'none', md: 'table-cell' } }}>
        <Chip label={item.Currency} size="small" variant="outlined" />
      </TableCell>

      <TableCell sx={{ minWidth: 100 }}>
        {isEditing.value ? (
          <CurrencyInput
            value={value}
            onChange={setValue}
            onBlur={() => handleSave('value')}
            currency={item.Currency}
            label=""
            size="small"
            variant="outlined"
            autoFocus
          />
        ) : (
          <Typography
            onClick={() => handleEditClick('value')}
            sx={{
              cursor: 'pointer',
              fontWeight: 500,
              color: 'error.main',
              '&:hover': { color: 'error.dark' },
            }}
          >
            {formatValue(value, item.Currency)}
          </Typography>
        )}
      </TableCell>

      <TableCell sx={{ minWidth: 120, display: { xs: 'none', sm: 'table-cell' } }}>
        {isEditing.debitDate ? (
          <TextField
            type="date"
            value={debitDate}
            onChange={(e) => setDebitDate(e.target.value)}
            onBlur={() => handleSave('debitDate')}
            onKeyDown={(e) => {
              if (e.key === 'Escape') handleCancelEdit('debitDate');
            }}
            autoFocus
            fullWidth
            size="small"
            variant="outlined"
          />
        ) : (
          <Typography
            onClick={() => handleEditClick('debitDate')}
            sx={{ cursor: 'pointer', '&:hover': { color: 'primary.main' } }}
          >
            {toDisplayDate(debitDate)}
          </Typography>
        )}
      </TableCell>

      <TableCell sx={{ minWidth: 150, display: { xs: 'none', lg: 'table-cell' } }}>
        {isEditing.observation ? (
          <TextField
            value={observation}
            onChange={(e) => setObservation(e.target.value)}
            onBlur={() => handleSave('observation')}
            onKeyDown={(e) => {
              if (e.key === 'Escape') handleCancelEdit('observation');
              if (e.key === 'Enter') handleSave('observation');
            }}
            autoFocus
            fullWidth
            size="small"
            variant="outlined"
            placeholder="Adicionar observacao..."
          />
        ) : (
          <Typography
            onClick={() => handleEditClick('observation')}
            sx={{
              cursor: 'pointer',
              color: observation ? 'text.primary' : 'text.secondary',
              fontStyle: observation ? 'normal' : 'italic',
              '&:hover': { color: 'primary.main' },
            }}
          >
            {observation || 'Sem observacao'}
          </Typography>
        )}
      </TableCell>

      <TableCell align="center">
        <Switch
          checked={isMandatory}
          onChange={handleMandatoryChange}
          color="primary"
          size="small"
        />
      </TableCell>

      <TableCell>
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <IconButton
            onClick={() => setExpanded(!expanded)}
            size="small"
            sx={{ mr: 0.5 }}
            aria-label={expanded ? 'Recolher' : 'Expandir'}
          >
            {expanded ? <ExpandLessIcon fontSize="small" /> : <ExpandMoreIcon fontSize="small" />}
          </IconButton>
          <IconButton onClick={() => setDeleteOpen(true)} color="error" size="small">
            <DeleteIcon fontSize="small" />
          </IconButton>
        </Box>
        <Dialog
          open={deleteOpen}
          onClose={() => setDeleteOpen(false)}
          aria-labelledby="delete-dialog-title"
          aria-describedby="delete-dialog-description"
        >
          <DialogTitle id="delete-dialog-title">Confirmar Exclusao</DialogTitle>
          <DialogContent>
            <DialogContentText id="delete-dialog-description">
              Tem certeza de que deseja excluir este lancamento?
            </DialogContentText>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setDeleteOpen(false)} color="primary">
              Cancelar
            </Button>
            <Button onClick={handleConfirmDelete} color="error" autoFocus>
              Excluir
            </Button>
          </DialogActions>
        </Dialog>
      </TableCell>
    </TableRow>

    <TableRow>
      <TableCell style={{ paddingBottom: 0, paddingTop: 0 }} colSpan={9}>
        <Collapse in={expanded} timeout="auto" unmountOnExit>
          <Box sx={{ py: 2, px: 2, bgcolor: 'action.hover', borderRadius: 1, my: 1 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
              <Typography variant="body2" color="text.secondary" sx={{ minWidth: 140 }}>
                Item do planejamento
              </Typography>
              <RecurringLinkSelect
                expense={item}
                items={recurringItems}
                onLinked={(id, recurringId) => onLinkUpdate?.(id, recurringId)}
                onError={onError}
              />
            </Box>
            <CorrelatedExpensesList
              key={correlationKey}
              parentId={item.ID}
              onAddCorrelation={() => setSelectorOpen(true)}
              onCorrelationChange={handleCorrelationChange}
              categories={categories}
              parentCurrency={item.Currency}
              parentDebitDate={debitDate}
              parentDescription={item.Description}
            />
          </Box>
        </Collapse>
      </TableCell>
    </TableRow>

    <CorrelationSelector
      open={selectorOpen}
      onClose={() => setSelectorOpen(false)}
      parentId={item.ID}
      parentDescription={item.Description}
      categories={categories}
      onCorrelationCreated={handleCorrelationChange}
    />
    </>
  );
});

export default EditableRow;
